// React
import { useState } from "react"

// Components
import { BaseTable, Button, Card, Checkbox, DateInput, FooterMenu, HeaderMenu, Input, Label, Loading, Modal, Select, TabSection, TableHeader, TableSection, Textarea } from "components"
import { Detail } from "../RecordReject/components"
import { Code, DetailReject, ReceivedDetail, Journal } from "./components"

// Contexts
import { DataContext, DetailContext, DropdownProvider } from "./contexts"

// Form
import { Controller, FormProvider, useFieldArray, useForm } from "react-hook-form"
import { ErrorMessage } from "@hookform/error-message"
import { yupResolver } from "@hookform/resolvers/yup"

// Icons
import { TbInfoCircle } from "react-icons/tb"

// Third-Party Libraries
import moment from "moment"
import toast from "react-hot-toast"
import { useNavigate, useParams } from "react-router-dom"

// Utils
import { convertNumber, useApi, useLocation, useToggle } from "utils"
import { default_option } from "pages/Purchase/utils"
import { useSupplier } from "../components/Supplier/utils"
import { billStatusList } from "../EnterBill/utils"
import { type FormType, useDetail, validationSchema, getSum, DetailType, receiveType } from "./utils"

export default function ReceivedRejectAdjustment(): JSX.Element {
  // Hooks
  const api = useApi()
  const navigate = useNavigate()
  const { id } = useParams()
  const { data, isLoading } = useDetail(id!)

  // Form
  const defaultValues: FormType = {
    memo: "",
    referensi: "",
    trx_received_code:"",
    transaction_date: moment().format("YYYY-MM-DD"),
    received_reject_detail: []
  }
  const methods = useForm<FormType>({
    defaultValues,
    resolver: yupResolver(validationSchema)
  })

  const { control, formState, watch } = methods
  const { fields, append, update } = useFieldArray({
    control,
    name: "received_reject_detail"
  })

  const receivedDetail = watch("received_reject_detail")
  const sum = getSum(receivedDetail ?? []) 

  const onSubmit = (value: FormType) => {
    return new Promise<void>((resolve) => {
      toast.promise(
        api.post(
          "/proreject/addreceivedreject",
          {
            ...value,
            reject_id: data?.reject_id,
            type_received: receiveType,
            vendor_id: data?.vendor_id,
            location_id: data?.location_id,
            address: data?.address
          }
        ),
        {
          loading: "Loading...",
          success: (res) => res.data.message,
          error: (err) => err.response.data.detail?.message ?? err.response.data.message
        }
      ).then(() => {
        navigate(`/purchase/transaction/record-reject/${id}`)
      }).catch(() => {
      }).finally(resolve)
    })
  }

  // Functions
  const onAdd = (value: DetailType): void => {
    if (fields.find(item => item.product_id === value.product_id)) {
      toast.error("Data has already added into detail table")
      return
    }

    append(value)
  }

  if (isLoading || !data) {
    return (
      <Loading
        errorText="No data available"
        loading={isLoading}
      />
    )
  }

  return (
    <div className="container my-5 flex flex-col gap-3">
      <FormProvider {...methods}>
        <HeaderMenu title="DATA ENTRY | REJECT - RECEIVED REJECT ADJUSTMENT">
          <Code
            reject_code={data.trx_code}
            jenis={receiveType}
          />
        </HeaderMenu>

        <section className="grid lg:grid-cols-2 gap-x-6 gap-y-3 items-end">
          <Supplier vendor_id={data.vendor_id} />

          <section className="lg:justify-self-end mb-3">
            <Checkbox
              readOnly
              value
            >
              TAX-INCLUSIVE
            </Checkbox>
          </section>
        </section>

        <Card>
          <Card.Body className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
            <section className="flex flex-col gap-3">
              <section>
                <Deliver location_id={data.location_id} />

                <Textarea
                  disabled
                  value={data.address}
                />
              </section>

              <DateInput
                disabled
                label="DATE REJECT"
                selected={new Date(data.transaction_date)}
                onChange={() => {}}
              />

              <section className="flex items-end">
                <section className="grow">
                  <Input
                    disabled
                    inputClass="rounded-r-none"
                    label="LINK TO REJECT ID"
                    value={data.trx_code}
                  />
                </section>

                <LinkToReject reject_id={data.reject_id} />
              </section>
            </section>

            <section className="flex flex-col gap-3">
              <Controller
                name="transaction_date"
                control={control}
                render={({ field, fieldState }) => (
                  <DateInput
                    label="DATE"
                    error={fieldState.error?.message}
                    selected={new Date(field.value)}
                    ref={field.ref}
                    onChange={(res) => field.onChange(moment(res).format("YYYY-MM-DD"))}
                  />
                )}
              />

              <Controller
                name="referensi"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    label="REFERENCE"
                    error={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                name="memo"
                control={control}
                render={({ field, fieldState }) => (
                  <Textarea
                    {...field}
                    label="MEMO"
                    error={fieldState.error?.message}
                  />
                )}
              />
            </section>
          </Card.Body>
        </Card>

        <DropdownProvider>
          <BaseTable>
            <TableHeader>
              <tr>
                <th>BACK DELIVERED</th>
                <th className="w-[150px]">QTY RECEIVED</th>
                <th>UNIT</th>
                <th>ITEM CODE</th>
                <th>DESCRIPTION</th>
                <th>PRICE</th>
                <th>DISC (%)</th>
                <th>TOTAL</th>
                <th>TAX</th>
                <th>ACTION</th>
              </tr>
            </TableHeader>

            <DetailContext.Provider value={{ fields, update }}>
              <ReceivedDetail />
            </DetailContext.Provider>
          </BaseTable>

          <ErrorMessage
            name="received_reject_detail"
            errors={formState.errors}
            render={({ message }) => (
              <Label
                error
                text={message}
              />
            )}
          />

          <section className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
            <section className="flex flex-col gap-3">
              <TableSection>
                <TableSection.Row
                  label="REJECT STATUS"
                  value={billStatusList[data.reject_status.toString()]}
                />

                <TableSection.Row
                  label="APPROVE STATUS"
                  value={default_option.find((item) => item.value === data.approve_status.toString())?.label ?? "-"}
                />

                <TableSection.Row
                  label="APPROVED BY"
                  value={data.approve_by}
                />
              </TableSection>
            </section>

            <section className="flex flex-col lg:items-end gap-3">
              <BaseTable
                plain
                className="w-full min-w-[100px] md:max-w-[400px] text-right">
                <tbody>
                  <tr>
                    <td className="w-[1px] whitespace-nowrap">SUBTOTAL</td>
                    <td>{convertNumber(sum.subtotal).intoCurrency}</td>
                  </tr>
                  <tr>
                    <td className="w-[1px] whitespace-nowrap">TAX</td>
                    <td>{convertNumber(sum.tax).intoCurrency}</td>
                  </tr>
                  <tr className="font-bold">
                    <td className="w-[1px] whitespace-nowrap">TOTAL RECEIVED</td>
                    <td>{convertNumber(sum.subtotal + sum.tax).intoCurrency}</td>
                  </tr>
                </tbody>
              </BaseTable>
            </section>
          </section>

          <DataContext.Provider
            value={{
              data,
              onAddDetail(value) {
                onAdd(value)
              }
            }}
          >
            <Tab />
          </DataContext.Provider>
        </DropdownProvider>
      

        <FooterMenu
          hasCancelButton
          attachment={{ actiontype: "attachment" }}
          customElement={{
            journal:<Journal data={data}/>
          }}
          save={{
            actiontype: "save",
            loading: methods.formState.isSubmitting ? "true" : undefined,
            onClick: methods.handleSubmit(onSubmit)
          }}
        />

    </FormProvider>
    </div>
  )
}

function Deliver(params: {
  location_id: number
}): JSX.Element {
  // Hooks
  const { data, isLoading } = useLocation()

  return (
    <Select
      isDisabled
      label="DELIVER TO"
      placeholder="Select Deliver"
      isLoading={isLoading}
      options={data}
      value={data.find(item => item.value === params.location_id.toString()) ?? null}
    />
  )
}

function LinkToReject(params: {
  reject_id: number
}): JSX.Element {
  // Hooks
  const { isActive, toggle } = useToggle(false)

  return (
    <>
      <Button
        type="button"
        color="primary"
        onClick={toggle}
      >
        <TbInfoCircle />
      </Button>

      {isActive && (
        <Modal
          isOpen
          title="REJECT ITEM DETAIL"
          closeModal={toggle}
        >
          <Modal.Body>
            <Detail id={params.reject_id.toString()} />
          </Modal.Body>
        </Modal>
      )}
    </>
  )
}

function Supplier(params: {
  vendor_id: number
}): JSX.Element {
  // Hooks
  const { data, isLoading } = useSupplier()

  return (
    <Select
      isDisabled
      label="SUPPLIER"
      placeholder="Select Supplier"
      isLoading={isLoading}
      options={data}
      value={data.find(item => item.value === params.vendor_id.toString()) ?? null}
    />
  )
}

function Tab(): JSX.Element {
  // Vars
  const tabList: string[] = ["DETAIL REJECT"]

  // Hooks
  const [currentTab, setCurrentTab] = useState<number>(0)

  return (
    <section>
      <TabSection
        activeIndex={currentTab}
        list={tabList}
        onChangeTab={setCurrentTab}
      />

      {Boolean(currentTab === 0) && <DetailReject />}
    </section>
  )
}